
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import Button, { SECONDARY_THEME, TERTIARY_THEME } from 'components/control/button';
import SelectField from 'components/control/select-field';
import { PaginationMode } from 'components/control/pagination-nav';
import { LocalizedInternalLinks } from 'components/home-page/internal-links';
import FilterIcon from 'components/icon/filter-icon';
import ListingsGrid from 'components/listings-grid';
import { SurroundingCities } from 'components/related-searches';
import { headerIds, searchFilterIds, testIds } from 'constants/test-constants';
import {
  GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_AFTER_JIGGLE,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_BEFORE_JIGGLE,
} from 'constants/events';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useExperiment } from '@growthbook/growthbook-react';
import { AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS, Filter, Sort } from 'contexts/preferences/listing-params/types';
import { useIsMobile } from 'hooks/use-size-class';
import React, { forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useThemeContext } from 'contexts';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import prioritizeFeeds from 'utils/prioritize-feeds';
import { sortOptions } from 'utils/select-options';
import ProviderDisclaimer from 'components/provider-disclaimer';
import Breadcrumbs, { type Breadcrumb } from 'components/breadcrumbs';
import styles from './style.module.scss';
import dynamic from 'next/dynamic';
import { InsightsResponse, SingleAddress } from '@zoocasa/go-search';
import AreaGuides from 'components/pages/area-listings/components/guides/area-guides';
import AreaFaq from '../v2/components/area-faq';
import { NamedContents } from 'components/named-content';
import { ListingFilterBar } from 'components/listing-filter-bar';
import { FilterChipBar } from 'components/listing-filter-bar/filter_chip_bar';
import { FilterItemData, getActiveFilters } from 'components/listing-filter-bar/listing_filter_bar';
import { useOnListingCardClick } from 'utils/listing-card';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'react-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import NextImage from 'next/image';
import {
  CALLOUT_EXPIRED_TYPE,
  CALLOUT_RENTED_TYPE,
  CALLOUT_RENT_TYPE,
  CALLOUT_SALE_TYPE,
  CALLOUT_SOLD_TYPE,
  CalloutType,
  FavouriteDelegate,
} from 'components/search-es/map/ClusterAnnotationCallout';
import { CompliancyDelegateType } from 'components/map-listing-card';

import type { LocalizedLinkData } from 'components/pages/area-listings/generate-dynamic-footer-data';
import type { ListingCardData } from 'components/listing-card';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import type { PartialDeep } from 'type-fest';
import type { LocalizedSeoLinkData } from 'data/seo-links';
import type { MapPageListing, MapPageCluster } from '@zoocasa/go-search';
import type { DisplayablePin } from 'domain/map-pin/map-pin.types';
import type { Bounds, LatLngPoint } from 'components/search-es/types';
import { ZOOCASA_TENANT_ID, isTenantExpCA } from '@tenants';
const SearchFilterDeprecationWarning = dynamic(() => import('components/search-filter-deprecation-warning'), { ssr: false });
const MyLinkMyLead = dynamic(() => import('components/pages/area-listings/components/my-link-my-lead'));
const MyLinkMyLeadCreate = dynamic(() => import('components/my-link-my-lead-create'));
const LocalizedProvinceCitySeoLinks = dynamic(() => import('components/pages/area-listings/components/province-city-seo-links').then(mod => ({ default: mod.LocalizedProvinceCitySeoLinks })));
const MapComponent = dynamic(async () => (await import('components/search-es/map/Map')), { ssr: false });

export interface Pagination {
  page: number;
  size: number;
  count: number;
  total: number;
  /** Sum of listingCount across the building-grouped pins currently rendered. Set only in pin mode. */
  currentListings?: number;
  /** Exact total of matching listings across the entire result set (response.totalListings). Set only in pin mode. */
  totalListings?: number;
}

export type AreaInsightsData = {
  areaData?: InsightsResponse;
  surroundingCities?: SurroundingCities[];
};

type MapComponentMethods = {
  changeLocation(location: LatLngPoint, zoom: number): void;
  clearAnnotations(): void;
};

export interface HybridPageLegacyProps {
  electedAddress: SingleAddress;
  listingParams: ListingParams;
  listings: ListingCardData[];
  breadcrumbs?: readonly Breadcrumb[];
  pagination: Pagination;
  sort: Sort;
  heading: string;
  citySlug?: string;
  // Map props
  clusteredListings?: MapPageCluster[];
  displayablePins?: DisplayablePin[];
  fetchBuildingListings?: (buildingKey: string, signal: AbortSignal) => Promise<MapPageListing[]>;
  isMapLoading?: boolean;
  mapCenterPoint?: LatLngPoint;
  mapZoom?: number;
  compliancyDelegate?: CompliancyDelegateType;
  favouriteDelegate?: FavouriteDelegate;
  onMapBoundingRegionChanged?: (oldBounds: Bounds | null, newBounds: Bounds) => void;
  onMapZoomChanged?: (oldZoom: number, newZoom: number) => void;
  onListingClick?: (listing: MapPageListing) => void;
  onListingAddressClick?: (listing: MapPageListing) => void;
  onTermsRequiredClick?: (listing: MapPageListing) => void;
  onSingleListingClusterClick?: (listing: MapPageListing) => void;
  areaPolygonWkt?: string;
  areaName?: string;
  areaBlurb?: string;
  areaBlurbHeading?: string;
  areaOverviewTitle?: string;
  areaNamedContent?: NamedContents;
  areaInsightsData?: AreaInsightsData;
  showMapCardExperimentView?: boolean;
  internalLinks?: LocalizedLinkData[];
  internalLinksHeading?: string;
  seoLinks?: LocalizedSeoLinkData;
  noListingsInAreaMessage?: string;
  hasNoListingsInArea?: boolean;
  missingAddressMessage?: string;
  expandGuidesByDefault?: boolean;
  showGuides?: boolean;
  showDisclaimer?: boolean;
  showFilterDeprecationWarning?: boolean;
  showNoImageListingsFilterWarning?: boolean;
  showsMyLinkMyLeadCreate?: boolean;
  showsMyLinkMyLead?: boolean;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  /** Numbered-pagination handler — used by pin mode to jump to a specific page. */
  setPageNumber?: (pageNumber: number) => void;
  /** Which pagination UI to render in the listings grid. Defaults to PREVNEXT. */
  paginationMode?: PaginationMode;
  onMobileFilterButtonClick(): void;
  onSortChanged(sort: Sort): void;
  onValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  onSaveSearchButtonClick: () => void;
  listingsContainerRef: React.RefObject<HTMLDivElement>;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onMapIconClick: () => void;
  onMoreButtonClick: () => void;
  onDeprecationWarningButtonClick?: () => void;
  onNoImageListingsFilterWarningClose?: () => void;
  trackMapCardExperimentView?: () => void;
  trackMapCardClick?: () => void;
  isProvinceOrCountryAddress?: boolean;
  isMobileListView: boolean;
  setIsMobileListView: (isMobileListView: boolean) => void;
  isCrawler?: boolean;
  mapkitSnapshotUrl?: string;
  isMapCenterPointLoading?: boolean;
  isMapCenterPointError?: boolean;
  mapCenterPointError?: Error;
  useLayoutV2?: boolean;
  /** When true, map uses building-grouped pins (carousel + viewport callbacks). */
  useBuildingGroupedPins?: boolean;
  onVisiblePinIdsChanged?: (ids: Set<number>) => void;
  isListingsLoading?: boolean;
  /** Fired when desktop users toggle "Hide Map" / full-width list (building pins + bounds handling in controller). */
  onSidePanelExpandedChange?: (expanded: boolean) => void;
}

export type HybridPageLegacyViewMethods = MapComponentMethods;

const HybridPageLegacy = forwardRef(function HybridPageLegacy(props: HybridPageLegacyProps, ref: React.ForwardedRef<HybridPageLegacyViewMethods>) {
  const {
    listingParams,
    listings,
    pagination: {
      page, count, total, size, currentListings, totalListings,
    },
    heading,
    breadcrumbs,
    listingsContainerRef,
    clusteredListings,
    displayablePins,
    fetchBuildingListings,
    isMapLoading,
    mapCenterPoint,
    mapZoom,
    compliancyDelegate,
    favouriteDelegate,
    onMapBoundingRegionChanged,
    onMapZoomChanged,
    onListingClick,
    onListingAddressClick,
    onTermsRequiredClick,
    onSingleListingClusterClick,
    areaPolygonWkt,
    areaName,
    areaBlurb,
    areaBlurbHeading,
    areaInsightsData,
    areaNamedContent,
    areaOverviewTitle,
    showMapCardExperimentView,
    internalLinks,
    internalLinksHeading,
    seoLinks,
    noListingsInAreaMessage,
    hasNoListingsInArea,
    missingAddressMessage,
    expandGuidesByDefault,
    showGuides,
    showDisclaimer,
    showFilterDeprecationWarning = false,
    showNoImageListingsFilterWarning = false,
    showsMyLinkMyLeadCreate = false,
    showsMyLinkMyLead = false,
    onPrevPage,
    onNextPage,
    setPageNumber,
    paginationMode = PaginationMode.PREVNEXT,
    onSortChanged,
    onMobileFilterButtonClick,
    onValueChange,
    onSaveSearchButtonClick,
    onLocationSearchClick,
    onMapIconClick,
    onMoreButtonClick,
    onDeprecationWarningButtonClick = () => { /* no-op */ },
    onNoImageListingsFilterWarningClose = () => { /* no-op */ },
    trackMapCardExperimentView,
    trackMapCardClick,
    isProvinceOrCountryAddress = false,
    isMobileListView,
    setIsMobileListView,
    isCrawler = false,
    mapkitSnapshotUrl,
    isMapCenterPointLoading,
    isMapCenterPointError,
    mapCenterPointError,
    useBuildingGroupedPins: _useBuildingGroupedPins = false,
    onVisiblePinIdsChanged,
    isListingsLoading = false,
    onSidePanelExpandedChange,
  } = props;
  const onListingCardClick = useOnListingCardClick();
  const isMobile = useIsMobile();
  const { formatNumber } = useI18nFormatting();
  const { tenantId } = useThemeContext();
  // Initialize with current filters from props
  const [activeFilterItems, setActiveFilterItems] = useState<FilterItemData[]>(() => getActiveFilters(listingParams.filter));
  const filterBarIsOpen = activeFilterItems.length !== 0;

  // Jiggle animation state
  const MAP_BUTTON_JIGGLE_FLAG = 'map-button-jiggle';
  const isJiggleExperimentActive = useExperiment({key: MAP_BUTTON_JIGGLE_FLAG, variations: [0, 1], weights: [0.5, 0.5]});
  const isZoocasaTenant = tenantId === ZOOCASA_TENANT_ID;
  const areaFaqEnabled = isTenantExpCA(tenantId);
  const [accumulatedScroll, setAccumulatedScroll] = useState(0);
  const [shouldJiggle, setShouldJiggle] = useState(false);
  const [hasJiggled, setHasJiggled] = useState(false);
  const [clickedBeforeJiggle, setClickedBeforeJiggle] = useState(false);
  const lastScrollY = useRef(0);
  const JIGGLE_SCROLL_THRESHOLD = typeof window !== 'undefined' ? window.innerHeight * 2 : 1000;
  
  
  // Track if map has been loaded on mobile
  const [hasLoadedMap, setHasLoadedMap] = useState(false);
  const providerIds = showDisclaimer ? prioritizeFeeds([...new Set(listings.map(l => l.providerId))]) : [];
  // In pin mode the controller fills currentListings/totalListings with exact counts from
  // /api/map/pins (totalListings, plus a sum of pin.listingCount for what's currently rendered).
  // When the result set spans more than one page (totalListings > currentListings), surface
  // "X of Y Listings Found" so users can see they're only viewing a subset.
  const hasPinListingTotals = currentListings != null && totalListings != null;
  const isPaginatedAcrossPages = hasPinListingTotals && totalListings > currentListings;
  const subheading = hasNoListingsInArea
    ? noListingsInAreaMessage
    : isPaginatedAcrossPages
      ? capitalizeWords(`${formatNumber(currentListings)} of ${formatNumber(totalListings)} listings found`)
      : capitalizeWords(`${formatNumber(count)}+ listings found`);
  const listingsGridParams = {
    page: {
      number: page,
      size: size,
    },
  };
  // In pin mode the controller passes an exact totalPages from /api/map/pins
  // (already clamped to MAX_PIN_OFFSET-reachable). In legacy/cluster mode the
  // value is cursor-derived, hence the +1 sentinel.
  const isNumberedPagination = paginationMode === PaginationMode.NUMBERED;
  const listingsGridMeta = {
    totalCount: count,
    totalPages: isNumberedPagination
      ? total
      : (total === 200 || total === 0 ? total : total + 1),
  };

  const sortingOptions = useMemo(() => sortOptions(listingParams.filter.status || AVAILABLE_STATUS), [listingParams.filter.status]);
  const showInternalLinks = internalLinks?.length > 0;
  const showSeoLinks = seoLinks?.links?.length > 0;
  const listingsGridBreadcrumbs = [...(breadcrumbs || [])];

  // Dynamic callout type logic (same as search page)
  const calloutType: CalloutType = useMemo(() => {
    let type: CalloutType = CALLOUT_EXPIRED_TYPE; // Default fallback
    const filters = listingParams.filter;

    if (filters.rental) {
      if (filters.status === NOT_AVAILABLE_SOLD_STATUS) {
        type = CALLOUT_RENTED_TYPE; // "X rented"
      } else if (filters.status === AVAILABLE_STATUS) {
        type = CALLOUT_RENT_TYPE; // "X rent"
      }
    } else if (filters.status === NOT_AVAILABLE_SOLD_STATUS) {
      type = CALLOUT_SOLD_TYPE; // "X sold"
    } else if (filters.status === AVAILABLE_STATUS) {
      type = CALLOUT_SALE_TYPE; // "X sale"
    }

    return type;
  }, [listingParams.filter]);
  const mapRef = useRef<MapComponentMethods | null>(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [isSidePanelExpanded, setIsSidePanelExpanded] = useState(false);
  useEffect(() => {
    onSidePanelExpandedChange?.(isSidePanelExpanded);
  }, [isSidePanelExpanded, onSidePanelExpandedChange]);
  const { t } = useTranslation(Namespaces.COMMON);
  const [highlightedListingId, setHighlightedListingId] = useState<number | null>(null);
  const [showMapControls, setShowMapControls] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);

  const handleLeftExpandClick = () => {
    if (isMapExpanded) {
      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST, 'Map Controls');
      setIsMapExpanded(false);
      setIsSidePanelExpanded(false);
      setShowMapControls(true);
    } else {
      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP, 'Map Controls');
      setIsMapExpanded(true);
      setIsSidePanelExpanded(false);
      setShowMapControls(true);
    }
  };

  const handleRightExpandClick = () => {
    if (isSidePanelExpanded) {
      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP, 'Map Controls');
      setIsMapExpanded(false);
      setIsSidePanelExpanded(false);
      setShowMapControls(true);
      window.scrollTo({
        top: 0,
      });
    } else {
      AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP, 'Map Controls');
      setIsMapExpanded(false);
      setIsSidePanelExpanded(true);
      setShowMapControls(true);
    }
  };

  useImperativeHandle(ref, () => ({
    changeLocation(location, zoom) {
      if (mapRef?.current?.changeLocation) {
        mapRef?.current?.changeLocation(location, zoom);
      }
    },
    clearAnnotations() {
      if (mapRef?.current?.clearAnnotations) {
        mapRef.current.clearAnnotations();
      }
    },
  }), []);

  const ProviderDisclaimerComponent = React.memo(
    ({ providerIds }: { providerIds: number[] }) => {
      return providerIds.map(p => <ProviderDisclaimer key={p} providerId={p} tenantName={'zoocasa'}/>);
    },
    (prevProps, nextProps) => {
      if (prevProps.providerIds.length !== nextProps.providerIds.length) {
        return false;
      }
      return prevProps.providerIds.every((id, index) => id === nextProps.providerIds[index]);
    }
  );

  useEffect(() => {
    setActiveFilterItems(getActiveFilters(listingParams.filter));
  }, [listingParams.filter]);

  useEffect(() => {
    if (isMobile && isMobileListView) {
      window.scrollTo({
        top: scrollPosition,
      });
    }
    else if (isMobile) {
      window.scrollTo({
        top: 0,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileListView]);

  useEffect(() => {
    if (isMapExpanded || (isMobile && !isMobileListView)) {
      document.body.style.overflow = 'hidden';
    } else {
      // Do not set `overflow: auto` — it overrides global `overflow-x: clip` on body
      // (application.scss) and re-enables horizontal scrolling when content is slightly wide.
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [isMapExpanded, isMobile, isMobileListView]);

  // Track scroll distance for jiggle animation
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);
    lastScrollY.current = currentScrollY;

    setAccumulatedScroll(prev => prev + scrollDelta);
  }, []);

  useEffect(() => {
    if (isJiggleExperimentActive.variationId !== 1 || !isZoocasaTenant || !isMobileListView || !isMobile) {
      return;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isJiggleExperimentActive.variationId === 1, isZoocasaTenant, isMobileListView, isMobile, handleScroll]);

  // Trigger jiggle when scroll threshold is reached
  useEffect(() => {
    if (accumulatedScroll >= JIGGLE_SCROLL_THRESHOLD && isJiggleExperimentActive.variationId === 1 && isZoocasaTenant && isMobileListView) {
      setShouldJiggle(true);
      setHasJiggled(true);
    }
  }, [accumulatedScroll, JIGGLE_SCROLL_THRESHOLD, isJiggleExperimentActive.variationId, isZoocasaTenant, isMobileListView]);

  // Reset scroll tracking after jiggle animation completes
  useEffect(() => {
    if (shouldJiggle) {
      const timer = setTimeout(() => {
        setShouldJiggle(false);
        setAccumulatedScroll(0);
      }, 600); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [shouldJiggle]);

  // Mark map as loaded once user views it (desktop or mobile map view)
  useEffect(() => {
    if (!isMobile || !isMobileListView) {
      setHasLoadedMap(true);
    }
  }, [isMobile, isMobileListView]);

  return (
    <div>
      <Breadcrumbs breadcrumbs={breadcrumbs || []} />
      <div className={styles.component}>
        <section className={styles['search-nav']}>
          <ListingFilterBar
            onLocationSearchClick={onLocationSearchClick}
            activeFilterItems={activeFilterItems}
            setActiveFilterItems={setActiveFilterItems}
            onValueChange={onValueChange}
            onSaveSearchButtonClick={onSaveSearchButtonClick}
            onMapIconClick={onMapIconClick}
            onMoreButtonClick={onMoreButtonClick}
            filters={listingParams.filter}
            showNoImageListingsFilterWarning={showNoImageListingsFilterWarning}
            onNoImageListingsFilterWarningClose={onNoImageListingsFilterWarningClose}
            hideMapViewButton={true}
            hideFilterChipBarSaveSearchButton={true}
          />
        </section>
        {showsMyLinkMyLead ?
          <MyLinkMyLead />
          :
          null
        }
        <div className={buildClassName(styles['content-container'], (isSidePanelExpanded || isProvinceOrCountryAddress) ? styles['full-height'] : activeFilterItems.length > 0 ? styles['screen-height-bump'] : styles['screen-height'], isProvinceOrCountryAddress && styles['province-full-width'])}>
          { !isCrawler &&
            <div className={buildClassName(styles['button-group-mobile'], shouldJiggle && styles['jiggle'], 'mobile-only')}>
              <Button //jiggle after scrolling
                theme={TERTIARY_THEME}
                label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_MOBILE_TOGGLE, { context: isMobileListView ? 'map' : 'list', defaultValue: 'Show Map' })}
                onClick={() => {
                  // Track analytics based on jiggle state
                  if (hasJiggled) {
                    // Button has jiggled, track click after jiggle
                    AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_AFTER_JIGGLE);
                  } else if (!clickedBeforeJiggle) {
                    // Button hasn't jiggled yet and hasn't been clicked before, track click before jiggle
                    AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE_BEFORE_JIGGLE);
                    setClickedBeforeJiggle(true);
                  }
                  // Always track the general event for backward compatibility
                  AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE);
                  if (isMobileListView) {
                    setScrollPosition(window.pageYOffset);
                  }
                  setIsMobileListView(!isMobileListView);
                }}
                testId={searchFilterIds.mapButtonAreaPage}
              />
              {!isMobileListView &&
                <Button
                  theme={SECONDARY_THEME}
                  label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_REMOVE_BOUNDARY, { defaultValue: 'Remove Boundary' })}
                  onClick={onMapIconClick}
                />
              }
            </div>
          } 
          {!isProvinceOrCountryAddress && showMapControls && !isCrawler &&
            <div className={buildClassName(styles['button-group'], 'desktop-only', activeFilterItems.length > 0 && styles['button-group-bump'], (isSidePanelExpanded || isMapExpanded || isProvinceOrCountryAddress) && styles['right-side-buttons'])}>
              {!(isSidePanelExpanded || isProvinceOrCountryAddress) &&
            <Button
              theme={SECONDARY_THEME}
              label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_LEFT_BUTTON, { context: isMapExpanded ? 'show_list' : 'expand_map', defaultValue: '< Expand Map' })}
              onClick={handleLeftExpandClick}
              testId={searchFilterIds.mapButtonAreaPage}
            />
              }
              {!isMapExpanded &&
              <Button
                theme={SECONDARY_THEME}
                label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_RIGHT_BUTTON, { context: isSidePanelExpanded ? 'show_map' : 'hide_map', defaultValue: 'Hide Map >' })}
                onClick={handleRightExpandClick}
              />
              }
              {!(isSidePanelExpanded || isProvinceOrCountryAddress) && !isMapExpanded &&
              <Button
                theme={SECONDARY_THEME}
                label={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_REMOVE_BOUNDARY, { defaultValue: 'Remove Boundary' })}
                onClick={onMapIconClick}
                testId={searchFilterIds.removeBoundaryButton}
              />
              }
            </div>
          }
          <div ref={listingsContainerRef} className={buildClassName(styles['area-page-content'], (isMapExpanded || (!isMobileListView && isMobile)) && styles['side-panel-hidden'])}>
            <div className={styles['title-sort-row']}>
              <div>
                <h1 className={styles['page-title']} data-testid={headerIds.pageHeading}>
                  {heading}
                </h1>
                {missingAddressMessage && (
                  <h2 className={styles['missing-address']}>
                    {missingAddressMessage}
                  </h2>
                )}
                <h2 className={styles['page-subtitle']}>
                  {/* While the pin fetch is in flight (building-grouped pins mode) `count`
                      still holds the unlimited SSR total, so show a loading label instead of
                      flashing e.g. "3000+ Listings Found" then dropping to the limited count. */}
                  {isListingsLoading ? t(LOCALE_COMMON_PATHS.LOADING, { defaultValue: 'Loading…' }) : subheading}
                </h2>
              </div>
              <div className={styles['sub-filter']}>
                <SelectField
                  className={styles['sort-by']}
                  testId={searchFilterIds.sortByDropdown}
                  ariaLabel={'Sort by dropdown'}
                  value={listingParams.sort}
                  options={sortingOptions}
                  onValueChange={onSortChanged}
                />
                <Button
                  className={styles['mobile-filter-btn']}
                  testId={searchFilterIds.mobileFilterButton}
                  theme={TERTIARY_THEME}
                  Icon={FilterIcon}
                  label="Filters"
                  onClick={onMobileFilterButtonClick}
                />
                {showsMyLinkMyLeadCreate ?
                  <MyLinkMyLeadCreate /> :
                  <></>
                }
              </div>
              {(isMobile && filterBarIsOpen) && <div className={styles['mobile-filter-chip-bar']}>
                <FilterChipBar
                  activeFilterItems={activeFilterItems}
                  setActiveFilterItems={setActiveFilterItems}
                  onValueChange={onValueChange}
                  onSaveSearchButtonClick={onSaveSearchButtonClick}
                  filters={listingParams.filter}
                  isMobile={isMobile}
                  hideSaveSearchButton={true}
                />
              </div>}
            </div>
            <ListingsGrid
              testId={testIds.listingGrid}
              listings={listings}
              useListingCardWithCompliance={false}
              isLoading={isListingsLoading}
              params={listingsGridParams}
              meta={listingsGridMeta}
              showMapCardExperimentView={showMapCardExperimentView}
              breadcrumbs={listingsGridBreadcrumbs}
              onPrevPage={onPrevPage}
              onNextPage={onNextPage}
              setPageNumber={setPageNumber}
              paginationMode={paginationMode}
              listingParams={listingParams}
              trackMapCardExperimentView={trackMapCardExperimentView}
              trackMapCardClick={trackMapCardClick}
              onListingCardClick={onListingCardClick}
              isOnSidePanel={!(isSidePanelExpanded || isProvinceOrCountryAddress)}
              onListingHover={setHighlightedListingId}
            />
            {areaFaqEnabled &&
            <AreaFaq
              areaName={areaName}
              listingCount={count}
              insightsData={areaInsightsData?.areaData}
            />}
            {showGuides &&
            <AreaGuides
              areaOverviewTitle={areaOverviewTitle}
              areaName={areaName}
              areaBlurbHeading={areaBlurbHeading}
              areaBlurb={areaBlurb}
              namedContent={areaNamedContent}
              isActive={expandGuidesByDefault}
              insightsData={areaInsightsData?.areaData}
            />
            }
            {showDisclaimer && <ProviderDisclaimerComponent providerIds={providerIds}/>}
            {showInternalLinks && <LocalizedInternalLinks isActive={expandGuidesByDefault} data={internalLinks} heading={internalLinksHeading} />}
            {showSeoLinks && <LocalizedProvinceCitySeoLinks seoLinks={seoLinks} />}

          </div>
          {!isCrawler ? (
            <div className={buildClassName(styles['map-container'], (isSidePanelExpanded || (isProvinceOrCountryAddress && !isMobile) || (isMobileListView && isMobile)) && styles['map-container-hidden'])}>
              {hasLoadedMap && (
                <Suspense fallback={<section className={styles['loading-map']} />}>
                  <MapComponent
                    ref={mapRef}
                    onSavedSearchFilterClick={onMoreButtonClick}
                    clusters={clusteredListings}
                    displayablePins={displayablePins}
                    highlightedListingId={highlightedListingId}
                    highlightedBuildingKey={null}
                    fetchBuildingListings={fetchBuildingListings}
                    showsLoading={isMapLoading}
                    showsSidePanel={false}
                    isSidePanelFullScreen={false}
                    center={mapCenterPoint}
                    zoom={mapZoom ?? listingParams.filter.zoom}
                    onMapBoundingRegionChanged={onMapBoundingRegionChanged}
                    onMapZoomChanged={onMapZoomChanged}
                    showDebugOverlay={false}
                    calloutType={calloutType}
                    compliancyDelegate={compliancyDelegate as CompliancyDelegateType}
                    favouriteDelegate={favouriteDelegate as FavouriteDelegate}
                    onListingClick={onListingClick}
                    onListingAddressClick={onListingAddressClick}
                    onTermsRequiredClick={onTermsRequiredClick}
                    onSingleListingClusterClick={onSingleListingClusterClick}
                    polygonWkt={isProvinceOrCountryAddress ? undefined : areaPolygonWkt}
                    isAiSearch={false}
                    isHybridPage={true}
                    setShowMapControls={setShowMapControls}
                    isMapCenterPointLoading={isMapCenterPointLoading}
                    isMapCenterPointError={isMapCenterPointError}
                    mapCenterPointError={mapCenterPointError}
                    onVisiblePinIdsChanged={onVisiblePinIdsChanged}
                  />
                </Suspense>
              )}
            </div>
          ) : (
            <div className={styles['static-map-container']}>
              <NextImage src={mapkitSnapshotUrl} alt={t(LOCALE_COMMON_PATHS.HYBRID_PAGE_MAP, { defaultValue: 'Map' })} width={640} height={480} layout='responsive' />
            </div>
          )}
        </div>

      </div>

      {showFilterDeprecationWarning &&
        <SearchFilterDeprecationWarning onClose={onDeprecationWarningButtonClick}/>
      }
    </div>);
});

export { HybridPageLegacy };
export default HybridPageLegacy;
